<template>
  <div class="card policy-chart-card h-100">
    <div class="card-header pb-0">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h6 class="mb-0">{{ $t("policyDynamics") }}</h6>
        <div class="bar-chart-legend">
          <div class="legend-item">
            <span class="legend-dot" style="background-color: #10B981;"></span>
            <span class="legend-text">{{ primaryLabel }}</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background-color: #EF4444;"></span>
            <span class="legend-text">{{ secondaryLabel }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body policy-chart-body">
      <div v-if="hasData" class="custom-bar-chart-wrap" @mouseleave="hoveredIndex = -1">
        <svg
          class="custom-bar-svg"
          viewBox="0 0 500 170"
          preserveAspectRatio="none"
        >
          <!-- Horizontal Grid Lines -->
          <g class="chart-grid">
            <line
              v-for="grid in gridLines"
              :key="'grid-' + grid.y"
              x1="30"
              :y1="grid.y"
              x2="485"
              :y2="grid.y"
              class="grid-line"
            />
          </g>

          <!-- Y Axis Labels -->
          <g class="chart-y-labels">
            <text
              v-for="grid in gridLines"
              :key="'ylabel-' + grid.y"
              x="22"
              :y="grid.y + 3.5"
              text-anchor="end"
              class="axis-tick-text"
            >{{ grid.value }}</text>
          </g>

          <!-- Bar Groups -->
          <g class="chart-bars">
            <g
              v-for="(grp, idx) in barGroups"
              :key="'grp-' + idx"
              @mouseenter="hoveredIndex = idx"
            >
              <!-- Hover background zone -->
              <rect
                :x="grp.centerX - grp.groupWidth / 2 - 8"
                y="15"
                :width="grp.groupWidth + 16"
                height="125"
                fill="transparent"
                class="bar-hover-zone"
                :class="{ 'is-active': hoveredIndex === idx }"
              />

              <!-- Primary Bar (New / Green) -->
              <rect
                :x="grp.b1.x"
                :y="grp.b1.y"
                :width="grp.b1.width"
                :height="grp.b1.height"
                rx="3"
                ry="3"
                fill="#10B981"
                class="bar-rect"
                :class="{ 'is-hovered': hoveredIndex === idx }"
              />

              <!-- Secondary Bar (Terminated / Red) -->
              <rect
                :x="grp.b2.x"
                :y="grp.b2.y"
                :width="grp.b2.width"
                :height="grp.b2.height"
                rx="3"
                ry="3"
                fill="#EF4444"
                class="bar-rect"
                :class="{ 'is-hovered': hoveredIndex === idx }"
              />

              <!-- X Axis Label -->
              <text
                :x="grp.centerX"
                y="158"
                text-anchor="middle"
                class="axis-tick-text"
                :class="{ 'is-active': hoveredIndex === idx }"
              >{{ grp.label }}</text>
            </g>
          </g>
        </svg>

        <!-- Floating Tooltip -->
        <div
          v-if="hoveredGroup"
          class="custom-bar-tooltip"
          :style="tooltipStyle"
        >
          <div class="tooltip-title">{{ hoveredGroup.label }}</div>
          <div class="tooltip-row">
            <span class="dot-sm" style="background-color: #10B981;"></span>
            <span>{{ primaryLabel }}:</span>
            <strong>{{ hoveredGroup.v1 }}</strong>
          </div>
          <div class="tooltip-row">
            <span class="dot-sm" style="background-color: #EF4444;"></span>
            <span>{{ secondaryLabel }}:</span>
            <strong>{{ hoveredGroup.v2 }}</strong>
          </div>
        </div>
      </div>

      <chart-empty-state
        v-else
        height="170px"
        icon="fas fa-chart-bar"
      />
    </div>
  </div>
</template>

<script>
import ChartEmptyState from "@/project/Global/Components/ChartEmptyState.vue";

export default {
  name: "BarChart",
  components: { ChartEmptyState },
  props: {
    chart: { type: Object, required: true },
    chart2: { type: Object, required: true },
  },
  data() {
    return {
      hoveredIndex: -1,
    };
  },
  computed: {
    primaryLabel() {
      return this.chart?.datasets?.label || this.$t("commonNew");
    },
    secondaryLabel() {
      return this.chart2?.datasets?.label || this.$t("commonTerminate");
    },
    labels() {
      return this.chart?.labels || [];
    },
    primaryData() {
      return (this.chart?.datasets?.data || []).map((v) => Number(v) || 0);
    },
    secondaryData() {
      return (this.chart2?.datasets?.data || []).map((v) => Number(v) || 0);
    },
    hasData() {
      const all = [...this.primaryData, ...this.secondaryData];
      if (!this.labels.length || !all.length) return false;
      return all.some((val) => val > 0);
    },
    maxValue() {
      const all = [...this.primaryData, ...this.secondaryData];
      const max = Math.max(...all, 0);
      if (max <= 0) return 5;
      if (max <= 5) return 5;
      if (max <= 10) return 10;
      if (max <= 20) return 20;
      return Math.ceil(max * 1.25);
    },
    gridLines() {
      const topY = 20;
      const bottomY = 140;
      const max = this.maxValue;
      return [
        { y: topY, value: max },
        { y: Math.round((topY + bottomY) / 2), value: Math.round(max / 2) },
        { y: bottomY, value: 0 },
      ];
    },
    barGroups() {
      const count = this.labels.length;
      if (count === 0) return [];

      const leftX = 45;
      const rightX = 475;
      const topY = 20;
      const bottomY = 140;
      const rangeY = bottomY - topY;
      const max = this.maxValue;

      const groupWidth = Math.min(48, Math.max(16, (rightX - leftX) / count - 14));
      const barWidth = Math.max(6, (groupWidth - 4) / 2);
      const stepX = (rightX - leftX) / count;

      return this.labels.map((label, i) => {
        const centerX = leftX + (i + 0.5) * stepX;
        const v1 = this.primaryData[i] || 0;
        const v2 = this.secondaryData[i] || 0;

        const h1 = max > 0 ? (v1 / max) * rangeY : 0;
        const h2 = max > 0 ? (v2 / max) * rangeY : 0;

        const minHeight = 2;
        const finalH1 = v1 > 0 ? Math.max(h1, minHeight) : 0;
        const finalH2 = v2 > 0 ? Math.max(h2, minHeight) : 0;

        const b1X = centerX - barWidth - 2;
        const b2X = centerX + 2;

        return {
          index: i,
          label,
          centerX: Math.round(centerX * 10) / 10,
          groupWidth,
          v1,
          v2,
          b1: {
            x: Math.round(b1X * 10) / 10,
            y: Math.round((bottomY - finalH1) * 10) / 10,
            width: Math.round(barWidth * 10) / 10,
            height: Math.round(finalH1 * 10) / 10,
          },
          b2: {
            x: Math.round(b2X * 10) / 10,
            y: Math.round((bottomY - finalH2) * 10) / 10,
            width: Math.round(barWidth * 10) / 10,
            height: Math.round(finalH2 * 10) / 10,
          },
        };
      });
    },
    hoveredGroup() {
      if (this.hoveredIndex >= 0 && this.hoveredIndex < this.barGroups.length) {
        return this.barGroups[this.hoveredIndex];
      }
      return null;
    },
    tooltipStyle() {
      if (!this.hoveredGroup) return {};
      const pctX = (this.hoveredGroup.centerX / 500) * 100;
      return {
        left: `${pctX}%`,
        top: "20px",
        transform: "translate(-50%, -100%)",
      };
    },
  },
};
</script>

<style scoped>
.bar-chart-legend {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.legend-dot {
  width: 7px;
  height: 7px;
  border-radius: 2px;
}

.legend-text {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--app-text, #4b5563);
}

.custom-bar-chart-wrap {
  position: relative;
  width: 100%;
  height: 170px;
  user-select: none;
}

.custom-bar-svg {
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

.grid-line {
  stroke: var(--app-border, rgba(120, 130, 145, 0.2));
  stroke-dasharray: 4 4;
  stroke-width: 1;
}

.axis-tick-text {
  font-size: 10px;
  fill: var(--app-text, #64748b);
  font-family: inherit;
  font-weight: 500;
  transition: fill 0.2s ease;
}

.axis-tick-text.is-active {
  fill: var(--app-heading, #111827);
  font-weight: 700;
}

.bar-hover-zone {
  cursor: pointer;
  border-radius: 4px;
  transition: fill 0.15s ease;
}

.bar-hover-zone.is-active {
  fill: var(--app-surface-soft, rgba(120, 130, 145, 0.08));
}

.bar-rect {
  transition: opacity 0.2s ease, filter 0.2s ease;
  cursor: pointer;
}

.bar-rect.is-hovered {
  filter: brightness(1.15) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.25));
}

.custom-bar-tooltip {
  position: absolute;
  pointer-events: none;
  background: var(--app-surface-elevated, #ffffff);
  border: 1px solid var(--app-border, #e2e8f0);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 11px;
  color: var(--app-heading, #111827);
  white-space: nowrap;
  box-shadow: var(--app-shadow-md, 0 4px 14px rgba(0, 0, 0, 0.15));
  z-index: 10;
  transition: left 0.15s ease;
}

.tooltip-title {
  font-size: 10px;
  color: var(--app-text, #64748b);
  margin-bottom: 3px;
  font-weight: 600;
}

.tooltip-row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  font-size: 11px;
  color: var(--app-heading, #111827);
}

.dot-sm {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
</style>
